import { useState } from 'react';
import { pathgen } from 'object-path-generator';
import { MenuItem, Select, KeyboardDatePicker, GridItem, useDevice, MobileSummaryCard, useOverlayContext, Box, Column, BottomSheetContentLayout } from 'seagull';
import { useIoContext, useStoreActions } from "../../contexts";
import { formatDateLong } from "../../models/time";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
var EffectiveDateType = /*#__PURE__*/function (EffectiveDateType) {
  EffectiveDateType["UponApproval"] = "uponApproval";
  EffectiveDateType["SpecificDate"] = "specificDate";
  return EffectiveDateType;
}(EffectiveDateType || {});
const valueToLabel = {
  [EffectiveDateType.UponApproval]: 'On acceptance',
  [EffectiveDateType.SpecificDate]: 'On a specific date'
};
const label = 'Agreement effective date';
function RelationshipEffectiveDateContent(props) {
  const {
    value,
    fullWidth,
    dateType,
    disabled,
    onSelectChange,
    onDateChange
  } = props;
  return /*#__PURE__*/_jsxs(Column, {
    gap: 8,
    width: "100%",
    children: [/*#__PURE__*/_jsx(Select, {
      collapseHelperText: false,
      SelectProps: {
        style: {
          textAlign: 'left'
        }
      },
      fullWidth: fullWidth,
      name: "hasEffectiveDate",
      value: dateType,
      displayValue: valueToLabel[dateType],
      disabled: disabled,
      onChange: e => onSelectChange(e.target.value),
      label: label,
      testId: RelationshipEffectiveDateTestIds.Dropdown.Root(),
      children: [{
        value: EffectiveDateType.UponApproval,
        testId: RelationshipEffectiveDateTestIds.Dropdown.Option.OnApproval()
      }, {
        value: EffectiveDateType.SpecificDate,
        testId: RelationshipEffectiveDateTestIds.Dropdown.Option.SpecificDate()
      }].map(_ref => {
        let {
          value,
          testId
        } = _ref;
        return /*#__PURE__*/_jsx(MenuItem, {
          value: value,
          testId: testId,
          children: valueToLabel[value]
        }, value);
      })
    }), /*#__PURE__*/_jsx("div", {
      hidden: value == null,
      children: /*#__PURE__*/_jsx(KeyboardDatePicker, {
        fullWidth: fullWidth,
        testId: RelationshipEffectiveDateTestIds.DatePicker(),
        KeyboardButtonProps: {
          'aria-label': 'change date'
        },
        disabled: dateType === EffectiveDateType.UponApproval || disabled,
        value: value,
        onChange: onDateChange
      })
    })]
  });
}
function useHandleFieldChanges(value) {
  const {
    logBI
  } = useStoreActions();
  const {
    getNow
  } = useIoContext();
  return {
    handleSelectChange(dateType) {
      if (dateType === EffectiveDateType.UponApproval) {
        logBI(10465);
        return null;
      } else {
        const effectiveDate = value || getNow();
        logBI(10468, {
          date: effectiveDate.toString()
        });
        return effectiveDate;
      }
    },
    handleDateChange(date) {
      if (date) {
        logBI(10468, {
          date: date.toString()
        });
      }
    }
  };
}
function RelationshipEffectiveDateInBottomSheet(props) {
  const {
    value,
    disabled,
    fullWidth,
    dateType,
    onChange
  } = props;
  const [tempValue, setTempValue] = useState(value);
  const [tempDateType, setTempDateType] = useState(dateType);
  const {
    hideBottomSheet
  } = useOverlayContext();
  const {
    handleSelectChange,
    handleDateChange
  } = useHandleFieldChanges(tempValue);
  function handleSave() {
    if (tempValue !== value) {
      onChange(tempValue);
    }
    hideBottomSheet();
  }
  return /*#__PURE__*/_jsx(BottomSheetContentLayout, {
    footer: {
      primaryAction: {
        text: 'Save',
        onClick: handleSave
      }
    },
    children: /*#__PURE__*/_jsx(Box, {
      padding: 16,
      children: /*#__PURE__*/_jsx(RelationshipEffectiveDateContent, {
        value: tempValue,
        fullWidth: fullWidth,
        dateType: tempDateType,
        disabled: disabled,
        onSelectChange: dateType => {
          setTempDateType(dateType);
          const newTempValue = handleSelectChange(dateType);
          setTempValue(newTempValue);
        },
        onDateChange: date => {
          setTempValue(date);
          handleDateChange(date);
        }
      })
    })
  });
}
export const RelationshipEffectiveDateTestIds = pathgen('RelationshipEffectiveDateTestIds');
export const RelationshipEffectiveDate = props => {
  const {
    value,
    disabled,
    onChange,
    fullWidth = false,
    className
  } = props;
  const dateType = value ? EffectiveDateType.SpecificDate : EffectiveDateType.UponApproval;
  const {
    isMobileDevice
  } = useDevice();
  const {
    pushBottomSheetPage
  } = useOverlayContext();
  const actualFullWidth = fullWidth || isMobileDevice;
  const {
    handleSelectChange,
    handleDateChange
  } = useHandleFieldChanges(value);
  return isMobileDevice ? /*#__PURE__*/_jsx(GridItem, {
    span: 12,
    className: className,
    "data-testid": RelationshipEffectiveDateTestIds.Root(),
    children: /*#__PURE__*/_jsx(MobileSummaryCard, {
      title: label,
      body: value ? formatDateLong(value) : valueToLabel[EffectiveDateType.UponApproval],
      editButtonTestId: RelationshipEffectiveDateTestIds.MobileCard.Edit(),
      bodyTestId: RelationshipEffectiveDateTestIds.MobileCard.Body(),
      onEdit: () => {
        pushBottomSheetPage({
          content: /*#__PURE__*/_jsx(RelationshipEffectiveDateInBottomSheet, {
            value: value,
            disabled: disabled,
            fullWidth: actualFullWidth,
            dateType: dateType,
            onChange: onChange
          })
        });
      }
    })
  }) : /*#__PURE__*/_jsxs(_Fragment, {
    children: [/*#__PURE__*/_jsx(GridItem, {
      span: actualFullWidth ? 12 : 9,
      className: className,
      testId: RelationshipEffectiveDateTestIds.Root(),
      children: /*#__PURE__*/_jsx(RelationshipEffectiveDateContent, {
        value: value,
        fullWidth: actualFullWidth,
        dateType: dateType,
        disabled: disabled,
        onSelectChange: dateType => {
          const newDate = handleSelectChange(dateType);
          onChange(newDate);
        },
        onDateChange: date => {
          onChange(date);
          handleDateChange(date);
        }
      })
    }), !actualFullWidth && /*#__PURE__*/_jsx(GridItem, {
      span: 3
    })]
  });
};